/**
 * User-facing tool catalog for the inspector's "Tools › All" view.
 *
 * This is NO LONGER hand-maintained. The data is generated from the agents repo
 * at build time: each agent's real `tools[]` joined with its user-facing copy
 * (`packages/runtime/src/tools/catalog-copy.ts`), emitted as `tool-manifest.json`
 * and synced here into `toolManifest.generated.json` via `npm run sync:tools`.
 * So the catalog can't drift from the agents' actual tool set — adding/removing a
 * tool there flows here on the next sync, and the agents' generator fails loudly
 * if a tool is added without catalog copy. See `docs/agent-console-data-path.md`.
 *
 * `requiresConfirmation` is now an explicit per-tool flag from the manifest (the
 * runtime knows which tools gate a destructive action), so the web no longer
 * infers it from confirmation parameters.
 */

import manifest from './toolManifest.generated.json';

export interface ToolInput {
  /** The input's name as the agent knows it. */
  readonly name: string;
  /** Whether the agent must supply it. */
  readonly required: boolean;
  /** Plain-English explanation for the user. */
  readonly description: string;
}

export interface ToolDoc {
  /** Tool name as it appears in the tool-use log. */
  readonly name: string;
  /** One-line summary shown on the collapsed row. */
  readonly summary: string;
  /** Fuller explanation shown when the row is expanded. */
  readonly description: string;
  /** The inputs the user-facing list explains. */
  readonly inputs: readonly ToolInput[];
  /** True when the tool gates a destructive action behind a yes/no confirm. */
  readonly requiresConfirmation?: boolean;
  /** Tiers T6/T8: callable by guests. Absent/`false` renders a locked row. */
  readonly guestEnabled?: boolean;
}

interface ToolManifest {
  readonly agents: Readonly<Record<string, { readonly tools: readonly ToolDoc[] }>>;
}

const MANIFEST = manifest as ToolManifest;

/** The user-facing tools an agent can call, in the agents' declaration order —
 *  `ToolsCatalog` sorts them A→Z for display. Empty for an unknown agent id. */
export function toolsForAgent(agentId: string): readonly ToolDoc[] {
  // The index can miss at runtime (unknown agent id) even though the
  // generated Record type says otherwise — typed honestly here.
  const entry = (MANIFEST.agents as Record<string, { tools: readonly ToolDoc[] } | undefined>)[agentId];
  return entry?.tools ?? [];
}

/** The inputs shown for a tool. (Confirmation gating is a flag now, not an
 *  input, so every listed input is user-facing.) */
export function visibleInputs(tool: ToolDoc): readonly ToolInput[] {
  return tool.inputs;
}

/** Whether the tool gates its action behind a confirmation step. */
export function requiresConfirmation(tool: ToolDoc): boolean {
  return tool.requiresConfirmation === true;
}
